/**
 * Los umbrales de alerta temprana de los signos vitales.
 *
 * Fuera del diálogo de triaje por dos razones. La primera, que dentro de un
 * `useMemo` no hay forma de probarlos, y son números sueltos que nadie revisa
 * al leer un diff. La segunda, que los MISMOS umbrales viven en la app del
 * personal (`Vitales.kt`): estando cada copia enterrada en su pantalla, el día
 * que alguien ajuste uno se quedarán en desacuerdo y el mismo paciente saldrá
 * en rojo en un aparato y normal en el otro.
 *
 * AVISA, NO BLOQUEA. Los rangos que acepta el servidor son amplios a propósito:
 * alguien PUEDE llegar con saturación de 41% y hay que poder registrarlo. Lo
 * que no puede es pasar sin que nadie lo mire — o el dato está mal tecleado
 * (141 escrito como 41) o el paciente está grave, y las dos cosas hay que
 * verlas antes de guardar.
 */

export interface SignosVitales {
  pa_sistolica?: number | null;
  pa_diastolica?: number | null;
  fc?: number | null;
  fr?: number | null;
  temperatura?: number | string | null;
  sat_o2?: number | null;
}

/** Lo que se sale de lo normal, en palabras y en el orden de gravedad. */
export function alertasDeVitales(v: SignosVitales, nivelUrgencia: number): string[] {
  const avisos: string[] = [];
  const { pa_sistolica: pas, pa_diastolica: pad, fc, fr, sat_o2: sat } = v;
  const temp = v.temperatura == null || v.temperatura === "" ? null : Number(v.temperatura);

  if (sat != null && sat < 90) avisos.push(`Saturación ${sat}% — hipoxemia.`);
  if (pas != null && pas < 90) avisos.push(`Presión sistólica ${pas} — hipotensión.`);
  if (pas != null && pas > 180) avisos.push(`Presión sistólica ${pas} — muy alta.`);
  if (pad != null && pad > 110) avisos.push(`Presión diastólica ${pad} — muy alta.`);
  if (fc != null && (fc < 50 || fc > 120)) avisos.push(`Frecuencia cardiaca ${fc} lpm.`);
  if (fr != null && (fr < 10 || fr > 24)) avisos.push(`Frecuencia respiratoria ${fr}.`);
  if (temp != null && !Number.isNaN(temp)) {
    if (temp >= 39) avisos.push(`Temperatura ${v.temperatura}° — fiebre alta.`);
    if (temp < 35) avisos.push(`Temperatura ${v.temperatura}° — hipotermia.`);
  }

  // Lo que de verdad importa: valores críticos con urgencia baja. O el dato
  // está mal escrito o el paciente no puede esperar en la cola.
  if (avisos.length > 0 && nivelUrgencia >= 4) {
    avisos.push("Con estos signos, el nivel de urgencia parece bajo. Compruébalo.");
  }
  return avisos;
}
